/**
 * The form-specific half of a Pub 1220 payee record, in the vocabulary the generators read.
 *
 * Positions 544 to 750 of the "B" record are where a form stops being a list of money and
 * starts being itself: a distribution code, a grant date, an insured person, a merchant
 * category. `pub1220Layouts.ts` says where those characters sit. This says what each one is
 * called once it is a record, so that reading a FIRE file and reading a spreadsheet land on
 * the same column names.
 *
 * Every column named here exists on that form's interface in `forms/{form}/interface/`, which
 * is what the XML generators read. Nothing is invented, and nothing is dropped silently: a
 * tail field with no home on the form is carried as PUB1220_{FieldName} rather than discarded.
 *
 * Two shapes of mapping:
 *  - `direct` carries the characters across unchanged, or through one of the converters below.
 *  - `coded` is for the Pub 1220 fields that are a code where the form is a set of checkboxes:
 *    1099-B's type of gain or loss, 1099-K's filer and payment type, 1099-LTC's status of
 *    illness. One character in, one or two columns out.
 */

/** A tail field that lands in exactly one column, optionally through a converter. */
export interface DirectTailField {
  column: string;
  /** How to read the characters. Default is the trimmed text. */
  as?: "text" | "date8" | "checkbox" | "integer";
}

export interface TailMapping {
  direct: Record<string, DirectTailField>;
  coded?: Record<string, (raw: string) => Record<string, string>>;
}

/** Pub 1220 writes dates as YYYYMMDD. Every generator wants YYYY-MM-DD. */
export function date8ToIso(raw: string): string | null {
  const digits = String(raw || "").replace(/\D/g, "");
  if (digits.length !== 8) return null;
  const [y, m, d] = [digits.slice(0, 4), digits.slice(4, 6), digits.slice(6, 8)];
  if (Number(m) < 1 || Number(m) > 12 || Number(d) < 1 || Number(d) > 31) return null;
  return `${y}-${m}-${d}`;
}

/**
 * Pub 1220 marks a box with "1" (or "2" for a second TIN notice). The forms carry checkboxes
 * as "X". A blank is not a "no", it is the absence of a mark, so it produces no column at all.
 */
export function checkboxValue(raw: string): string | null {
  const value = String(raw || "").trim();
  if (!value || value === "0") return null;
  return "X";
}

/** A zero-filled count, without the leading zeros. */
export function integerValue(raw: string): string | null {
  const digits = String(raw || "").replace(/\D/g, "");
  if (!digits || /^0+$/.test(digits)) return null;
  return String(Number(digits));
}

const text = (column: string): DirectTailField => ({ column, as: "text" });
const date = (column: string): DirectTailField => ({ column, as: "date8" });
const check = (column: string): DirectTailField => ({ column, as: "checkbox" });
const count = (column: string): DirectTailField => ({ column, as: "integer" });

/** Turn a one-character Pub 1220 code into the form's own checkbox columns. */
function pick(map: Record<string, string[]>): (raw: string) => Record<string, string> {
  return (raw: string) => {
    const columns = map[String(raw || "").trim()];
    if (!columns) return {};
    return Object.fromEntries(columns.map((c) => [c, "X"]));
  };
}

export const TAIL_MAPPING_BY_FORM: Record<string, TailMapping> = {
  "1098-E": {
    direct: {
      OriginationFees_CapitalizedInterestIndicator: check("BOX2_LOAN_ORIGINATION_FEES_IND"),
    },
  },

  "1098-T": {
    direct: {
      StudentTINSolicitation: check("SSN_SOLICITED"),
      HalfTimeStuIndicator: check("BOX8_HALF_TIME_STUDENT_IND"),
      GraduateStuInd: check("BOX9_GRADUATE_STUDENT_IND"),
      AcademicPeriodInd: check("BOX7_ACADEMIC_PERIOD_IND"),
    },
  },

  "1099-B": {
    direct: {
      NoncoveredSecurityIndicator: check("BOX_5_NONCOVERED_SECURITY_INDICATOR"),
      DateSoldOrDisposed: date("BOX_1C_DATE_SOLD"),
      CUSIPNumber: text("CUSIP_NUMBER"),
      DescriptionOfProperty: text("BOX_1A_DESCRIPTION"),
      DateAcquired: date("BOX_1B_DATE_ACQUIRED"),
      LossNotAllowedIndicator: check("BOX_7_LOSS_NOT_ALLOWED_INDICATOR"),
      // A, B, D, E or X - the form carries the letter itself, not a checkmark.
      Form8949CheckboxIndicator: text("FORM_8949_CHECKBOX"),
      CollectablesCheckboxIndicator: check("BOX_3_PROCEEDS_COLLECTIBLES"),
      FATCAFilingRequirementIndicator: check("FATCA_REQUIREMENT"),
      QOFCheckboxIndicator: check("BOX_3_PROCEEDS_QOF"),
    },
    coded: {
      // Position 546. Ordinary is its own box on the form, so 3 and 4 set two columns.
      TypeOfGainOrLossIndicator: pick({
        "1": ["BOX_2_SHORT_TERM_INDICATOR"],
        "2": ["BOX_2_LONG_TERM_INDICATOR"],
        "3": ["BOX_2_SHORT_TERM_INDICATOR", "BOX_2_ORDINARY_INDICATOR"],
        "4": ["BOX_2_LONG_TERM_INDICATOR", "BOX_2_ORDINARY_INDICATOR"],
      }),
      // Position 547. 1 is gross, 2 is net of commissions and option premiums.
      GrossProceedsIndicator: pick({
        "1": ["BOX_6_GROSS_PROCEEDS_INDICATOR"],
        "2": ["BOX_6_NET_PROCEEDS_INDICATOR"],
      }),
    },
  },

  "1099-C": {
    direct: {
      // A through H. The generator validates the letter and falls back to A.
      IdentifiableEventCode: text("BOX6_IDENTIFIABLE_EVENT_CODE"),
      IdentifiableDate: date("BOX1_DATE_OF_IDENTIFIABLE_EVENT"),
      DebtDescription: text("BOX4_DEBT_DESCRIPTION"),
      // Pub 1220: "1" if personally liable, blank if not. A blank is left alone rather than
      // written as a "no", because a filer who never populated the field means neither.
      PersonalLiabilityIndicator: check("BOX5_BORROWER_LIABLE_YES_IND"),
    },
  },

  "1099-DIV": {
    direct: {
      SecondTINNotice: check("BOX_2ND_TIN_NOT_IND"),
      ForeignCountry: text("BOX8_FOREIGN_COUNTRY"),
      FatcaRequirement: check("BOX11_FATCA_REQUIREMENT"),
    },
  },

  "1099-INT": {
    direct: {
      SecondTINNotice: check("BOX_2ND_TIN_NOT_IND"),
      ForeignCountryorU_S_Possession: text("BOX7_FOREIGN_COUNTRY_US_POSSESSION"),
      CUSIPNumber: text("BOX14_TAX_EXEMPT_BOND_CUSIP"),
      FATCAFilingRequirementIndicator: check("FATCA_REQUIREMENT"),
    },
  },

  "1099-K": {
    direct: {
      NumberPaymentTransaction: count("BOX3_NO_PAYMENT_TRANSACTIONS"),
      PSENameTelephone: text("PSE_NAME"),
      MerchantCategoryCode: text("BOX2_MERCHANT_CAT_CODE"),
    },
    coded: {
      // Position 547: 1 is a payment settlement entity, 2 an electronic payment facilitator.
      FilerTypeIndicator: pick({
        "1": ["TYPE_OF_FILER_INDICATOR_PSE"],
        "2": ["TYPE_OF_FILER_INDICATOR_3RD_PARTY"],
      }),
      // Position 548: 1 is a payment card, 2 a third party network payment.
      PaymentTypeIndicator: pick({
        "1": ["TYPE_OF_PAYMENT_INDICATOR_PAYMENT_CARD"],
        "2": ["TYPE_OF_PAYMENT_INDICATOR_THIRD_PARTY"],
      }),
    },
  },

  "1099-LTC": {
    direct: {
      // 1 is per diem, 2 is a reimbursed amount.
      PaymentTypeIndicator: text("PAYMENT_TYPE"),
      InsuredSSN: text("INSURED_SSN"),
      InsuredName: text("INSURED_NAME"),
      InsuredAddress: text("INSURED_STREET_ADDRESS_1"),
      InsuredCity: text("INSURED_CITY"),
      InsuredState: text("INSURED_STATE"),
      InsuredZipCode: text("INSURED_ZIP_CODE"),
      DateCertified: date("DATE_CERTIFIED"),
      QualifiedContractIndicator: check("QUALIFIED_CONTRACT"),
    },
    coded: {
      StatusofIllnessIndicator: pick({
        "1": ["CHRONICALLY_ILL"],
        "2": ["TERMINAL_ILL"],
      }),
    },
  },

  "1099-MISC": {
    direct: {
      SecondTINNotice: check("BOX_2ND_TIN_NOT_IND"),
      DirectSalesIndicator: check("BOX7_DIRECT_SALES_IND"),
      FatcaFilingRequirementIndicator: check("BOX13_FATCA_REQUIREMENT"),
    },
  },

  "1099-NEC": {
    direct: {
      DirectSalesIndicator: check("BOX2_DIRECT_SALES"),
    },
  },

  "1099-R": {
    direct: {
      // One or two letters, and required by the schema. Y (qualified charitable distribution)
      // was added for TY2025.
      DistributionCode: text("BOX7_DISTRIBUTION_CODES"),
      TaxableAmountNotDeterminedIndicator: check("BOX2B_TAXABLE_AMOUNT_IND"),
      IRA_SEP_SIMPLEIndicator: check("BOX7_IRA_SEP_SIMPLE_IND"),
      TotalDistributionIndicator: check("BOX2B_TOTAL_DISTRIBUTION_IND"),
      PercentageofTotalDistribution: count("BOX9A_PERCENT_OF_DISTRIBUTION"),
      FirstYearofDesignatedRothContribution: text("BOX11_ROTH_CONTRIBUTION"),
      FATCAFilingRequirementIndicator: check("BOX12_FATCA_REQUIREMENT"),
      DateofPayment: date("BOX13_DATE_OF_PAYMENT"),
    },
  },

  "1099-SA": {
    direct: {
      DistributionCode: text("BOX3_DISTRIBUTION_CODE"),
      MedicareAdvantageMSAIndicator: check("BOX5_MA_MSA"),
      HSAIndicator: check("BOX5_HSA"),
      ArcherMSAIndicator: check("BOX5_ARCHER_MSA"),
    },
  },

  "1099-Q": {
    direct: {
      // Position 549. "1" means the recipient is not the designated beneficiary.
      DesignatedBeneficiaryIndicator: check("BOX6_NOT_DESIGNATED_BENEFICIARY"),
    },
    coded: {
      // Position 547. 1 is a trustee-to-trustee transfer (box 4a), 2 a QTP to Roth IRA
      // transfer (box 4b). One field, so a 1220 file can never tick both.
      TrusteeToTrusteeTransferIndicator: pick({
        "1": ["BOX4A_TRUSTEE_TO_TRUSTEE_TRANSFER"],
        "2": ["BOX4B_QTP_TO_ROTH_IRA_TRANSFER"],
      }),
      // Position 548, box 5: 1 private program, 2 state program, 3 Coverdell ESA.
      TypeOfTuitionPayment: pick({
        "1": ["BOX5_PRIVATE_QTP"],
        "2": ["BOX5_STATE_QTP"],
        "3": ["BOX5_COVERDELL_ESA"],
      }),
    },
  },

  "3921": {
    direct: {
      DateOptionGranted: date("BOX1_DATE_GRANTED"),
      DateOptionExercised: date("BOX2_DATE_EXERCISED"),
      NumberofSharesTransferred: count("BOX5_SHARES_TRANSFERRED"),
      IfOtherThanTransferorInformation: text("BOX6_COMPANY"),
    },
  },

  "3922": {
    direct: {
      DateOptionGrantedToTransferrer: date("BOX1_DATE_GRANTED"),
      DateOptionExercisedByTransferrer: date("BOX2_DATE_EXERCISED"),
      NoOfSharesTransferred: count("BOX6_NO_SHARES_TRANSFERRED"),
      LegalDateTransferredByTransferror: date("BOX7_DATE_TITLE_TRANSFERRED"),
    },
  },

  "5498-SA": {
    direct: {
      MedicareAdvantageMSAIndicator: check("BOX6_MA_MSA"),
      HSAIndicator: check("BOX6_HSA"),
      ArcherMSAIndicator: check("BOX6_ARCHER_MSA"),
    },
  },
};

/**
 * Tail fields that are read, understood, and deliberately not written to a column, because the
 * form has nowhere to put them. They are carried as PUB1220_{FieldName} so a converted file
 * never loses a character the filer wrote, and so the exception report can say so.
 */
export const TAIL_CARRIED_ONLY: Record<string, string[]> = {
  "1099-B": ["SecondTINNotice"],
  "1099-K": ["SecondTINNotice"],
  "1099-NEC": ["SecondTINNotice"],
};

/** Read one tail field the way its mapping says to. Null means "no value here". */
export function readTailValue(field: DirectTailField, raw: string): string | null {
  const value = String(raw || "").trim();
  if (!value) return null;
  switch (field.as) {
    case "date8":
      return date8ToIso(value);
    case "checkbox":
      return checkboxValue(value);
    case "integer":
      return integerValue(value);
    default:
      return value;
  }
}
